import type { UpdatesConfig } from '@urql/exchange-graphcache';

import type {
  AddLineItemToCartMutation,
  ApplyRewardMutation,
  EditLineItemInCartMutation,
  RemoveFromCartMutation,
  RemoveRewardMutation,
  ReorderLineItemMutation,
  ReorderOrderMutation,
  SubmitOrderMutation,
} from '@order/graphql';

import {
  deleteBillingAccount,
  invalidateCartCacheEntry,
  submitBraintreeBillingAccount,
  updateCartCache,
} from './utils';
import { addAddress, deleteAddress, updateAddress } from './utils/addresses';
import {
  addSubscriptionPaymentMethod,
  removeSubscriptionPaymentMethod,
  updateSubscriptionPaymentMethod,
} from './utils/sweetpass';

/**
 * Graphcache | Updates
 * A nested mapping of updater functions for mutation (and subscription) fields,
 * which may be used to add side-effects that update other parts of the cache
 * when the given subscription or mutation field is written to the cache.
 *
 * @see {@link: https://formidable.com/open-source/urql/docs/graphcache/cache-updates/}
 */

export const updates: UpdatesConfig = {
  Mutation: {
    // this force invalidates all the cache entries on logout
    logout(_result, _args, cache, _info) {
      cache.invalidate('Query');
      cache.invalidate('Mutation');
      cache.invalidate('Subscription');
    },

    //
    // ─── CART UPDATES ────────────────────────────────────────────────
    //

    /*
      The following helpers modifies the 'cart' cache entry for the respective mutations.

      urql uses the `__typename` and `id` fields to update the cache entries,
      but sometimes we need to override the cart entry when the ID is different;
      therefore, we must manually handle these updates.

      Add here any cart-related update handler.
     */

    addLineItemToCart(result: AddLineItemToCartMutation, _args, cache) {
      updateCartCache(cache, result.addLineItemToCart);
    },
    editLineItemInCart(result: EditLineItemInCartMutation, _args, cache) {
      updateCartCache(cache, result.editLineItemInCart);
    },
    removeFromCart(result: RemoveFromCartMutation, _args, cache) {
      updateCartCache(cache, result.removeFromCart);
    },
    applyReward(result: ApplyRewardMutation, _args, cache) {
      updateCartCache(cache, result.applyReward);
    },
    removeReward(result: RemoveRewardMutation, _args, cache) {
      updateCartCache(cache, result.removeReward);
    },
    reorderOrder(result: ReorderOrderMutation, _args, cache) {
      updateCartCache(cache, result.reorderOrder);
    },
    reorderLineItem(result: ReorderLineItemMutation, _args, cache) {
      updateCartCache(cache, result.reorderLineItem);
    },
    submitOrderUsingBillingAccount(result: SubmitOrderMutation, _args, cache) {
      invalidateCartCacheEntry(cache, result.submitOrderUsingBillingAccount);
    },

    // ─── Billing Accounts Cache ────────────────────────────────────────

    submitBraintreeBillingAccount,
    deleteBillingAccount,

    // ─── Addresses Cache ───────────────────────────────────────────────

    addAddress,
    updateAddress,
    deleteAddress,

    // ─── Subscription Payment Methods Cache ────────────────────────────

    addSubscriptionPaymentMethod,
    updateSubscriptionPaymentMethod,
    removeSubscriptionPaymentMethod,
  },
  Subscription: {},
};
